import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import cn from 'classnames'
import { LoadingOutlined } from '@ant-design/icons'
import { readTaskTrackerFilterInputValue } from '../../../../../lib/taskTrackerFilter.utils'
import classes from './index.module.css'

type TaskStickerEditableValueProps = {
  fieldSettingId: number
  fieldType: 'text' | 'number'
  value: string | null
  saving: boolean
  disabled?: boolean
  onSave: (
    fieldSettingId: number,
    value: string | null,
  ) => void | Promise<boolean | void>
}

const LoadingIcon = LoadingOutlined as unknown as FC<{
  spin?: boolean
  className?: string
}>

const formatDisplayValue = (
  fieldType: 'text' | 'number',
  value: string | null,
) => {
  const trimmed = value?.trim()
  if (!trimmed) {
    return fieldType === 'number' ? 'Не указано' : 'Не указано'
  }

  return trimmed
}

const TaskStickerEditableValue: FC<TaskStickerEditableValueProps> = ({
  fieldSettingId,
  fieldType,
  value,
  saving,
  disabled = false,
  onSave,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [draftValue, setDraftValue] = useState(value ?? '')

  const displayValue = formatDisplayValue(fieldType, value)
  const isEmpty = !value?.trim()

  useEffect(() => {
    setIsEditing(false)
    setDraftValue(value ?? '')
  }, [fieldSettingId, value])

  useEffect(() => {
    if (!isEditing) {
      setDraftValue(value ?? '')
    }
  }, [isEditing, value])

  useEffect(() => {
    if (!isEditing) return
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [isEditing])

  const handleStartEdit = useCallback(() => {
    if (saving || disabled) return
    setDraftValue(value ?? '')
    setIsEditing(true)
  }, [disabled, saving, value])

  const handleCancelEdit = useCallback(() => {
    setDraftValue(value ?? '')
    setIsEditing(false)
  }, [value])

  const handleSave = useCallback(async () => {
    if (saving) return

    const normalizedDraft = draftValue.trim()
    const normalizedCurrent = value?.trim() ?? ''

    if (normalizedDraft === normalizedCurrent) {
      setIsEditing(false)
      return
    }

    const saved = await onSave(fieldSettingId, normalizedDraft || null)
    if (saved !== false) {
      setIsEditing(false)
    }
  }, [draftValue, fieldSettingId, onSave, saving, value])

  const handleBlur = useCallback(() => {
    if (saving) return
    void handleSave()
  }, [handleSave, saving])

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setDraftValue(readTaskTrackerFilterInputValue(event))
  }, [])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        handleCancelEdit()
        return
      }

      if (event.key === 'Enter') {
        event.preventDefault()
        event.currentTarget.blur()
      }
    },
    [handleCancelEdit],
  )

  if (isEditing) {
    return (
      <div
        className={cn(classes.editableValueRow, {
          [classes.editableValueRowSaving]: saving,
        })}
      >
        <input
          ref={inputRef}
          className={classes.editableValueInput}
          type={fieldType === 'number' ? 'number' : 'text'}
          inputMode={fieldType === 'number' ? 'decimal' : 'text'}
          step={fieldType === 'number' ? 'any' : undefined}
          value={draftValue}
          disabled={saving}
          placeholder={fieldType === 'number' ? 'Введите число' : 'Не указано'}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
        {saving ? <LoadingIcon spin className={classes.editableValueSavingIcon} /> : null}
      </div>
    )
  }

  return (
    <button
      type="button"
      className={cn(classes.editableValueButton, {
        [classes.editableValueButtonEmpty]: isEmpty,
        [classes.editableValueButtonSaving]: saving,
        [classes.editableValueButtonLocked]: disabled && !saving,
      })}
      disabled={saving}
      aria-disabled={disabled || saving || undefined}
      onClick={handleStartEdit}
    >
      <span className={classes.editableValueText}>{displayValue}</span>
      {saving ? (
        <LoadingIcon spin className={classes.editableValueSavingIcon} />
      ) : null}
    </button>
  )
}

export default TaskStickerEditableValue
